// BatterySelection.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../FishFinderBuilder.css';
import { batteriesData } from '../data/productData';
import ImageModal from './ImageModal';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  padding: 20px 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 80px;
  background-color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;

  @media (min-width: 769px) {
    height: 100px;
  }
`;

const ProductImage = styled.img`
  width: 85%;
  height: 85%;
  object-fit: contain;

  @media (min-width: 769px) {
    width: 95%;
    height: 95%;
  }
`;

const BatterySelection = ({
  battery = null,
  setBattery = () => {},
  prevStep = () => {},
  nextStep = () => {},
  resetAll = () => {},
  jumpToStep = () => {},
  isInModal = false,
  onNavigateToFullPage = () => {}
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get builder state from location (for screen info)
  const builderState = location.state || {};
  
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Debug logging to check what state is being received
  useEffect(() => {
    console.log("BuilderState received in BatterySelection:", builderState);
    console.log("Transducer info:", builderState?.transducer);
  }, [builderState]);
  
  // Track which images have already tried fallbacks to prevent infinite loops
  const [failedImages, setFailedImages] = useState({});
  
  // Add modal state
  const [modalImage, setModalImage] = useState({
    isOpen: false,
    url: '',
    alt: ''
  });

  // Track expanded battery cards
  const [expandedCards, setExpandedCards] = useState(new Set());

  // Track selected battery including the variant (with/without charger)
  const [selectedVariantId, setSelectedVariantId] = useState(battery?.id || null);
  
  // Add charger toggle state for each battery, with default state based on any existing selection
  const [batteryStates, setBatteryStates] = useState(() => {
    // Start with only batteries that have charger options set to "without charger"
    const initialState = batteriesData.reduce((acc, curr) => {
      // Only add to state if the battery has charger options
      if (curr.hasChargerOption) {
        acc[curr.id] = { includeCharger: false };
      }
      return acc;
    }, {});

    // If there's a selected battery with charger, update its state
    if (battery && battery.includeCharger) {
      // Find the base battery ID that corresponds to this variant
      const baseBatteryId = batteriesData.find(b => 
        // Match by name prefix since variant names typically include the base name
        (battery.name && battery.name.includes(b.name)) ||
        // Or match by ID prefix for batteries with variant IDs
        (b.hasChargerOption && b.variants.withCharger.id === battery.id) ||
        (b.hasChargerOption && b.variants.withoutCharger.id === battery.id) ||
        // Or direct ID match for non-variant batteries
        b.id === battery.id
      )?.id;
      
      // If we found a matching battery with charger options, set its charger state
      if (baseBatteryId && batteriesData.find(b => b.id === baseBatteryId)?.hasChargerOption) {
        initialState[baseBatteryId] = { includeCharger: true };
      }
    }
    
    return initialState;
  });

  // Log the initial state and debug information on component mount
  useEffect(() => {
    if (battery) {
      console.log("Initial battery:", battery);
      console.log("Initial selectedVariantId:", selectedVariantId);
      console.log("Initial batteryStates:", batteryStates);
      
      // If the battery has a different variant ID than what's currently selected,
      // update the selectedVariantId
      if (battery.id !== selectedVariantId) {
        setSelectedVariantId(battery.id);
      }
    }
  }, []);

  // Handle image errors with fallback images from the data
  const handleImageError = (e, batteryName) => {
    if (failedImages[batteryName]) return;
    
    console.error(`Image failed to load for ${batteryName}: ${e.target.src}`);
    setFailedImages(prev => ({...prev, [batteryName]: true}));
    
    const batteryData = batteriesData.find(b => b.name === batteryName);
    
    if (batteryData && batteryData.fallbackImage) {
      e.target.src = batteryData.fallbackImage;
      return;
    }
    
    e.target.src = `https://dummyimage.com/150x100/cccccc/000000&text=${batteryName.replace(/\s+/g, '+')}`;
    e.target.onerror = null;
  };

  // Handle selection of a battery
  const handleBatterySelect = (selectedBattery) => {
    // Create the battery object with all necessary properties
    let batteryToSelect;
    let variantId;
    
    if (selectedBattery.hasChargerOption) {
      // For batteries with charger options, check the charger state
      const includeCharger = batteryStates[selectedBattery.id]?.includeCharger || false;
      const variant = includeCharger ? selectedBattery.variants.withCharger : selectedBattery.variants.withoutCharger;
      variantId = variant.id;
      batteryToSelect = {
        ...variant,
        image: selectedBattery.image,
        fallbackImage: selectedBattery.fallbackImage,
        id: variant.id,
        hasChargerOption: true,
        includeCharger: includeCharger,
        type: selectedBattery.type,
        capacity: selectedBattery.capacity,
        nominalVoltage: selectedBattery.nominalVoltage,
        wattHours: selectedBattery.wattHours
      };
    } else {
      // For batteries without charger options (charger always included)
      variantId = selectedBattery.id;
      batteryToSelect = {
        ...selectedBattery,
        hasChargerOption: false,
        includeCharger: true // Always true for batteries with charger included
      };
    }
    
    // Update selected variant ID for UI visual feedback
    setSelectedVariantId(variantId);
    
    // Then set the battery in the parent component
    setBattery(batteryToSelect);
  };

  // Handle charger toggle for a specific battery
  const handleChargerToggle = (batteryOption, include) => {
    // Update the charger state
    setBatteryStates(prev => ({
      ...prev,
      [batteryOption.id]: { ...prev[batteryOption.id], includeCharger: include }
    }));

    // Get the current variant ID
    const currentVariant = batteryOption.hasChargerOption
      ? (include ? batteryOption.variants.withCharger : batteryOption.variants.withoutCharger)
      : batteryOption;
    
    // Only update the current battery selection if this exact battery variant was previously selected
    if (selectedVariantId === (include ? batteryOption.variants.withCharger.id : batteryOption.variants.withoutCharger.id) ||
        selectedVariantId === batteryOption.id) {
      // Update the selected variant ID
      setSelectedVariantId(currentVariant.id);
      
      // Update the battery in the parent component
    if (batteryOption.hasChargerOption) {
      setBattery({
          ...currentVariant,
        image: batteryOption.image,
          fallbackImage: batteryOption.fallbackImage,
          hasChargerOption: true,
          includeCharger: include,
          type: batteryOption.type,
          capacity: batteryOption.capacity,
          nominalVoltage: batteryOption.nominalVoltage,
          wattHours: batteryOption.wattHours
        });
      }
    }
  };

  // Add filter state
  const [filters, setFilters] = useState({});
  
  // Create filtered batteries array
  const [filteredBatteries, setFilteredBatteries] = useState(batteriesData);
  
  // Update filtered batteries when filters change
  useEffect(() => {
    let filtered = batteriesData.filter(battery => {
      // Filter by battery type
      if (filters.type && !filters.type.includes(battery.type)) {
        return false;
      }
      
      // Filter by Bluetooth capability
      if (filters.bluetooth && !battery.hasBluetooth) {
        return false;
      }
      
      // Filter by minimum runtime
      if (filters.minRuntime && builderState?.selectedScreen) {
        // Calculate runtime for this battery
        const wattHours = parseFloat(battery.wattHours.replace(/[^\d.]/g, ''));
        
        // Get power draw in watts (Amps * 12V)
        const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
        const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
        const screenPowerDrawAmps = screenPowerDrawMatch 
          ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
          : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
        
        // Add transducer power draw if present
        const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
        const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
        const transducerPowerDrawAmps = transducerPowerDrawMatch 
          ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
          : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
        
        // Convert amps to watts using typical 12V system and combine
        const totalPowerDrawWatts = (screenPowerDrawAmps + transducerPowerDrawAmps) * 12;
        
        // Runtime calculation with 80% battery capacity
        const runtimeHours = (wattHours * 0.8) / totalPowerDrawWatts;
        
        // Check if runtime meets the minimum requirement
        if (runtimeHours < filters.minRuntime) {
          return false;
        }
      }
      
      return true;
    });
    
    setFilteredBatteries(filtered);
  }, [filters, builderState?.selectedScreen, builderState?.transducer]);

  // Function to toggle card expansion
  const toggleCardExpansion = (batteryId, event) => {
    // Prevent the click from triggering parent elements
    event.stopPropagation();
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(batteryId)) {
        newSet.delete(batteryId);
      } else {
        newSet.add(batteryId);
      }
      return newSet;
    });
  };

  // Tooltip component
  const Tooltip = ({ text }) => {
    const [visible, setVisible] = useState(false);
    const iconRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (visible && iconRef.current) {
        const rect = iconRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY + 6, // 6px gap
          left: rect.left + window.scrollX + rect.width / 2,
        });
      }
    }, [visible]);

    return (
      <>
        <span
          ref={iconRef}
          style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          tabIndex={0}
          onFocus={() => setVisible(true)}
          onBlur={() => setVisible(false)}
        >
          <span style={{ color: '#4CAF50', marginLeft: 4, fontWeight: 'bold' }}>ⓘ</span>
        </span>
        {visible && createPortal(
          <span style={{
            minWidth: '180px',
            maxWidth: '260px',
            backgroundColor: '#222',
            color: '#fff',
            textAlign: 'left',
            borderRadius: '6px',
            padding: '8px',
            position: 'absolute',
            zIndex: 2000,
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
            opacity: 1,
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            whiteSpace: 'normal',
            pointerEvents: 'none',
          }}>
            {text}
          </span>,
          document.body
        )}
      </>
    );
  };

    return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      overflow: 'hidden'
    }}>
      {/* Back to Builder button - outside and above everything else */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={prevStep}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content with Sidebar */}
      <div style={{
        display: 'flex',
        minHeight: 'calc(100vh - 80px)',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Sidebar */}
        <div style={{
          width: '30%',
          backgroundColor: '#1a1a1a',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
          position: 'sticky',
          top: '0',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 12px',
          overflowX: 'hidden',
          margin: 0
        }}>
          <h3 style={{ 
            fontSize: '13px',
            margin: '0 0 10px 0',
            color: '#ffffff',
            fontWeight: '600'
          }}>Filter Options</h3>
          
            <div style={{
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '0 0 10px 0',
            flex: 1,
            maxWidth: '100%'
          }}>
            {/* Filters Section */}
            <div className="filter-section" style={{ 
              marginBottom: '15px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              {/* Battery Type Filter */}
              <div style={{ marginBottom: '12px' }}>
                <h5 style={{ 
                  fontSize: '10px',
                  margin: '0 0 4px 0',
                  color: '#b8c7d9',
                  fontWeight: '400'
                }}>Battery Type</h5>
                <div style={{ 
                  display: 'flex', 
                  gap: '8px',
                  flexWrap: 'wrap',
                  maxWidth: '100%'
                }}>
                  <label style={{
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!filters?.type || filters?.type.includes('LiFePO4')}
                      onChange={() => {
                        setFilters(prev => {
                          const currentTypes = prev?.type || ['LiFePO4', 'NMC'];
                          const newTypes = currentTypes.includes('LiFePO4')
                            ? currentTypes.filter(t => t !== 'LiFePO4')
                            : [...currentTypes, 'LiFePO4'];
                          return { ...prev, type: newTypes.length ? newTypes : null };
                        });
                      }}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    LiFePO4
                  </label>
                  <label style={{
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e0e0e0',
                    cursor: 'pointer'
                  }}>
                    <input
                      type="checkbox"
                      checked={!filters?.type || filters?.type.includes('NMC')}
                      onChange={() => {
                        setFilters(prev => {
                          const currentTypes = prev?.type || ['LiFePO4', 'NMC'];
                          const newTypes = currentTypes.includes('NMC')
                            ? currentTypes.filter(t => t !== 'NMC')
                            : [...currentTypes, 'NMC'];
                          return { ...prev, type: newTypes.length ? newTypes : null };
                        });
                      }}
                      style={{
                        width: '12px',
                        height: '12px',
                        cursor: 'pointer'
                      }}
                    />
                    NMC
                  </label>
            </div>
              </div>
              
              {/* Bluetooth Filter */}
              <div style={{ marginBottom: '12px' }}>
                <h5 style={{ 
                  fontSize: '11px',
                  margin: '0 0 4px 0',
            color: '#b8c7d9',
                  fontWeight: '400'
                }}>Features</h5>
                <label style={{
                  fontSize: '11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters?.bluetooth || false}
                    onChange={(e) => {
                      setFilters(prev => ({
                        ...prev,
                        bluetooth: e.target.checked
                      }));
                    }}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  Bluetooth Enabled
                </label>
              </div>
              
              {/* Min Runtime Filter */}
              {builderState?.selectedScreen && (
                <div style={{ marginBottom: '12px' }}>
                  <h5 style={{ 
                    fontSize: '11px',
                    margin: '0 0 4px 0',
                    color: '#b8c7d9',
                    fontWeight: '400'
                  }}>Minimum Runtime</h5>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <input
                      type="range"
                      min="0"
                      max="48"
                      step="1"
                      value={filters?.minRuntime || 0}
                      onChange={(e) => {
                        setFilters(prev => ({
                          ...prev,
                          minRuntime: parseInt(e.target.value)
                        }));
                      }}
                      style={{
                        width: '100%',
                        cursor: 'pointer'
                      }}
                    />
          <div style={{
                      fontSize: '11px',
                      color: '#e0e0e0',
            display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>Min: {filters?.minRuntime || 0} hours</span>
            <button
                        onClick={() => setFilters(prev => ({ ...prev, minRuntime: 0 }))}
              style={{
                          background: 'transparent',
                border: 'none',
                          color: '#b8c7d9',
                          fontSize: '10px',
                cursor: 'pointer',
                          padding: '0',
                          textDecoration: 'underline'
                        }}
                      >
                        Reset
            </button>
                    </div>
                  </div>
            </div>
          )}

              {/* Reset All Filters Button */}
            <button
                onClick={() => setFilters({})}
              style={{
                  width: '100%',
                  padding: '6px',
                  backgroundColor: 'rgba(0, 0, 0, 0.3)',
                  color: '#b8c7d9',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  fontSize: '11px',
                cursor: 'pointer',
                  marginTop: '4px'
              }}
            >
                Reset All Filters
            </button>
          </div>

            {/* Battery Runtime Calculator Heading */}
          <div style={{ 
              backgroundColor: '#131313',
              margin: '15px -12px 15px -12px',
              padding: '12px 12px 0 12px',
              borderTop: '1px solid rgba(255, 255, 255, 0.05)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              <h3 style={{ 
                fontSize: '13px',
                margin: '0 0 12px 0',
                color: '#ffffff',
                fontWeight: '600',
              display: 'flex', 
              alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ color: '#4CAF50', fontSize: '14px' }}>⚡</span>
                Battery Runtime
              </h3>

            {/* If a screen is selected, show power draw and runtime estimates */}
            {builderState?.selectedScreen ? (
              <>
                <div style={{ 
            marginBottom: '15px',
                  paddingBottom: '15px',
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                }}>
                  {/* Selected Screen */}
                  <h4 style={{ 
                    fontSize: '12px',
                    marginBottom: '8px',
                    color: '#b8c7d9',
                    fontWeight: '500'
                  }}>Selected Components</h4>
                  <div style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.3)',
                    padding: '8px',
                    borderRadius: '6px',
                    marginBottom: '8px'
                  }}>
                    <div style={{ 
                      fontSize: '11px', 
                      color: '#ffffff', 
                      fontWeight: '500',
                      whiteSpace: 'normal',
                      overflowWrap: 'break-word',
                      lineHeight: '1.3'
                    }}>
                      {builderState.selectedScreen.name}
                    </div>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
                      marginTop: '4px'
                    }}>
                      <div style={{ fontSize: '10px', color: '#4CAF50', fontWeight: '500' }}>
                        Power Draw: <span style={{ color: '#ffffff' }}>
                          {builderState.selectedScreen.powerDraw12V || '0.8-1.2'} A @ 12V
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Show Transducer if selected */}
                  {builderState?.transducer && (
                    <div style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.3)',
              padding: '8px',
                      borderRadius: '6px',
                      marginBottom: '8px'
                    }}>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#ffffff', 
                        fontWeight: '500',
                        whiteSpace: 'normal',
                        overflowWrap: 'break-word',
                        lineHeight: '1.3'
                      }}>
                        {builderState.transducer.name}
            </div>
            <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        marginTop: '4px'
                      }}>
                        <div style={{ fontSize: '10px', color: '#4CAF50', fontWeight: '500' }}>
                          Power Draw: <span style={{ color: '#ffffff' }}>
                            {builderState.transducer.powerDraw12V || '0'} A @ 12V
                          </span>
            </div>
          </div>
                    </div>
                  )}

                  {/* Total Power Draw */}
                  {builderState?.transducer && (
            <div style={{
                      backgroundColor: builderState.transducer.type === 'livescope' ? 'rgba(255, 213, 79, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                      padding: '8px',
                      borderRadius: '6px',
                      marginTop: '8px',
                      marginBottom: '8px',
                      border: builderState.transducer.type === 'livescope' ? '1px solid rgba(255, 213, 79, 0.3)' : '1px solid rgba(76, 175, 80, 0.2)'
                    }}>
                      <div style={{ 
                        fontSize: '10px', 
                        color: builderState.transducer.type === 'livescope' ? '#ffd54f' : '#4CAF50', 
                        fontWeight: '500',
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '4px'
                      }}>
                        <span>Total Power Draw:</span>
                        <span>
                          {(() => {
                            // Calculate total power draw
                            const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
                            const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                            const screenPowerDrawAmps = screenPowerDrawMatch 
                              ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
                              : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
                            
                            const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
                            const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                            const transducerPowerDrawAmps = transducerPowerDrawMatch 
                              ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
                              : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
                            
                            const totalPowerDraw = screenPowerDrawAmps + transducerPowerDrawAmps;
                            return `${totalPowerDraw.toFixed(1)} A @ 12V`;
                          })()}
                        </span>
                      </div>
                      
                      {builderState.transducer.type === 'livescope' && (
                        <div style={{ 
                          fontSize: '9px', 
                          color: '#e0e0e0', 
                          fontStyle: 'italic',
                          marginTop: '2px',
                          textAlign: 'center'
                        }}>
                          High power draw will significantly reduce battery runtime
            </div>
          )}
                    </div>
                  )}
                </div>

          <div style={{
                  backgroundColor: 'rgba(73, 177, 76, 0.05)', 
                  borderLeft: '2px solid #4CAF50',
                  padding: '10px 12px',
                  borderRadius: '3px',
                  fontSize: '11px',
                  color: '#b8c7d9',
                  lineHeight: '1.4',
                  marginBottom: '12px'
                }}>
                  <p style={{ margin: '0 0 8px 0' }}><strong style={{ color: '#4CAF50' }}>Battery Runtime Info:</strong></p>
                  <p style={{ margin: '0 0 8px 0' }}>Runtime estimates are shown on each battery based on the combined power draw of selected components.</p>
                  {builderState?.transducer?.type === 'livescope' && (
                    <p style={{ margin: '0 0 8px 0', color: '#ffd54f' }}>
                      <strong>Note:</strong> Live imaging transducers draw significant power, which can reduce battery runtime by 50% or more compared to standard transducers.
                    </p>
                  )}
                  <p style={{ margin: '0' }}>Calculations use 80% of battery capacity for a conservative estimate. Actual runtime may vary based on settings, brightness, range, and usage conditions.</p>
                </div>
              </>
            ) : (
              <div style={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                padding: '15px',
                borderRadius: '6px',
            display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                color: '#b8c7d9',
                fontSize: '11px',
                lineHeight: '1.4',
            marginBottom: '12px'
          }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>⚡</div>
                <p style={{ margin: '0 0 15px 0', fontSize: '11px' }}>
                  <strong style={{ color: '#ffffff' }}>No screen selected</strong>
                </p>
                <p style={{ margin: '0 0 15px 0', fontSize: '11px' }}>
                  Select a screen to see battery life estimates for each battery option.
                </p>
              <button
                  onClick={() => navigate('/compare-screens', {
                    state: { returnTo: '/battery-selection' }
                  })}
                style={{
                    background: '#4CAF50',
                  border: 'none',
                    color: '#fff',
                    fontSize: '11px',
                  cursor: 'pointer',
                    padding: '8px 12px',
                    borderRadius: '4px',
                  fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '5px',
                    width: '100%'
                  }}
                >
                  Select a Screen <span>→</span>
              </button>
              </div>
            )}
            </div>
          </div>
        </div>
        
        {/* Main Content Area */}
        <div style={{
          width: '70%',
          padding: '0 20px 20px 20px',
        }}>
          <h1 style={{ 
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>Battery Selection</h1>

          {filteredBatteries.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔋</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Batteries Found</h3>
              <p style={{ 
                margin: '0 0 16px 0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more battery options. Consider changing the battery type or minimum runtime requirement.
              </p>
              <button
                onClick={() => setFilters({})}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'rgba(76, 175, 80, 0.2)',
                  color: '#4CAF50',
                  border: '1px solid #4CAF50',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Reset All Filters
              </button>
            </div>
          ) : (
            <GridContainer>
              {filteredBatteries.map(batteryOption => {
                // For batteries without charger options, includeCharger is not relevant
                const includeCharger = batteryOption.hasChargerOption ? 
                  batteryStates[batteryOption.id]?.includeCharger : false;
                
                // Get the appropriate variant based on charger selection
                const currentVariant = batteryOption.hasChargerOption
                  ? (includeCharger ? batteryOption.variants.withCharger : batteryOption.variants.withoutCharger)
                  : batteryOption;
                
                // Get the variant ID for selection comparison
                const currentVariantId = currentVariant.id;
                
                // Check if this exact variant is selected
                const isSelected = selectedVariantId === currentVariantId;
                
                return (
                  <div 
                    id={`battery-card-${batteryOption.id}`}
                    key={batteryOption.id} 
                    style={{
                      backgroundColor: '#1a1a1a',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      width: '100%',
                      transition: 'box-shadow 0.3s ease'
                    }}
                  >
                    <ImageContainer 
                      onClick={() => setModalImage({ 
                        isOpen: true, 
                        url: batteryOption.image, 
                        alt: batteryOption.name 
                      })}
                    >
                      <ProductImage 
                        src={batteryOption.image} 
                        alt={batteryOption.name}
                        onError={(e) => handleImageError(e, batteryOption.name)}
                      />
                      {/* Add Bluetooth icon for batteries with Bluetooth capability */}
                      {batteryOption.hasBluetooth && (
                        <div style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          backgroundColor: 'rgba(25, 118, 210, 0.8)',
                          borderRadius: '50%',
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg 
                            width="16" 
                            height="16" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="white" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          >
                            <polyline points="6.5 6.5 17.5 17.5 12 23 12 1 17.5 6.5 6.5 17.5"></polyline>
                          </svg>
                        </div>
                      )}
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <svg 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="white" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <circle cx="11" cy="11" r="8"></circle>
                          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                      </div>
                    </ImageContainer>

                    <div style={{ padding: '12px' }}>
                      <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>
                        {currentVariant.name}
                      </h3>
                      {(batteryOption.dealInfo || batteryOption.name.toLowerCase().includes('amped outdoors')) ? (
                        <div style={{ fontSize: '0.85em', margin: '0 0 4px 0', display: 'flex', alignItems: 'center' }}>
                          <span style={{ textDecoration: 'line-through', color: '#b8c7d9', marginRight: '8px' }}>
                            ${currentVariant.msrp.toFixed(2)}
                          </span>
                          <span style={{ color: '#4CAF50', fontWeight: '500', display: 'flex', alignItems: 'center', gap: 4 }}>
                            ${(currentVariant.msrp * 0.9).toFixed(2)}
                            <Tooltip text={'💰 This may not be the best price! Select your components, then click Find Best Price on the bundle builder page.'} />
                          </span>
                        </div>
                      ) : (
                        <p style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 4 }}>
                          ${currentVariant.msrp.toFixed(2)}
                          <Tooltip text={'💰 This may not be the best price! Select your components, then click Find Best Price on the bundle builder page.'} />
                        </p>
                      )}

                      {/* Display deal info for all Amped Outdoors batteries or any battery with dealInfo */}
                      {(batteryOption.dealInfo || batteryOption.name.toLowerCase().includes('amped outdoors')) && (
    <div style={{ 
                          display: 'inline-block',
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          padding: '2px 6px',
                          marginBottom: '8px',
                          borderRadius: '3px',
                          fontSize: '0.7em'
                        }}>
                          <span style={{ fontWeight: 'bold', color: '#4CAF50' }}>EXCLUSIVE DEAL:</span>{' '}
                          <span style={{ color: '#ffffff' }}>10% OFF with our link</span>
                        </div>
                      )}

                      {/* Charger Toggle Bar - only show for batteries with charger options */}
                      {batteryOption.hasChargerOption && (
                        <div style={{
                          display: 'flex',
                          gap: '1px',
                          marginBottom: '10px',
                          backgroundColor: '#233446',
                          padding: '1px',
                          borderRadius: '4px',
                          width: '100%'
                        }}>
      <button
                            onClick={() => handleChargerToggle(batteryOption, false)}
        style={{
                              flex: 1,
                              padding: '6px 8px',
          border: 'none',
                              borderRadius: '3px',
          cursor: 'pointer',
                              backgroundColor: !includeCharger ? '#4CAF50' : 'transparent',
                              color: !includeCharger ? 'white' : '#b8c7d9',
                              transition: 'all 0.2s ease',
                              fontSize: '0.8em'
                            }}
                          >
                            Without Charger
      </button>
                          <button
                            onClick={() => handleChargerToggle(batteryOption, true)}
                            style={{
                              flex: 1,
                              padding: '6px 8px',
                              border: 'none',
                              borderRadius: '3px',
                              cursor: 'pointer',
                              backgroundColor: includeCharger ? '#4CAF50' : 'transparent',
                              color: includeCharger ? 'white' : '#b8c7d9',
                              transition: 'all 0.2s ease',
                              fontSize: '0.8em'
                            }}
                          >
                            With Charger
                          </button>
                        </div>
                      )}
                      {!batteryOption.hasChargerOption && (
                        <div style={{ 
                          marginBottom: '10px',
                          backgroundColor: 'rgba(76, 175, 80, 0.1)',
                          padding: '8px',
                          borderRadius: '4px',
                          fontSize: '0.8em',
                          color: '#4CAF50',
                          textAlign: 'center'
                        }}>
                          Charger included
                        </div>
                      )}

                      {/* Battery Specs - Now with expandable section */}
                      <div className="battery-specs" style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '0',
                        fontSize: '0.9em',
                        marginBottom: '10px'
                      }}>
                        {/* Always visible specs - Most important first */}
                        <div className="spec-row" style={{
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Est. Runtime:</span>
                          {builderState?.selectedScreen ? (
                            (() => {
                              // Calculate runtime for this battery
                              const wattHours = parseFloat(batteryOption.wattHours.replace(/[^\d.]/g, ''));
                              
                              // Get screen power draw in amps
                              const screenPowerDrawStr = builderState.selectedScreen?.powerDraw12V || '1.0';
                              const screenPowerDrawMatch = screenPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                              const screenPowerDrawAmps = screenPowerDrawMatch 
                                ? (parseFloat(screenPowerDrawMatch[1]) + parseFloat(screenPowerDrawMatch[3])) / 2 
                                : parseFloat(screenPowerDrawStr.replace(/[^\d.]/g, '') || 1.0);
                              
                              // Add transducer power draw if present
                              const transducerPowerDrawStr = builderState.transducer?.powerDraw12V || '0';
                              const transducerPowerDrawMatch = transducerPowerDrawStr.toString().match(/(\d+(\.\d+)?)-(\d+(\.\d+)?)/);
                              const transducerPowerDrawAmps = transducerPowerDrawMatch 
                                ? (parseFloat(transducerPowerDrawMatch[1]) + parseFloat(transducerPowerDrawMatch[3])) / 2 
                                : parseFloat(transducerPowerDrawStr.replace(/[^\d.]/g, '') || 0);
                              
                              // Convert amps to watts using typical 12V system
                              const totalPowerDrawWatts = (screenPowerDrawAmps + transducerPowerDrawAmps) * 12;
                              
                              // Runtime calculation with 80% battery capacity
                              const runtimeHours = (wattHours * 0.8) / totalPowerDrawWatts;
                              const hours = Math.floor(runtimeHours);
                              const minutes = Math.round((runtimeHours - hours) * 60);
                              
                              return (
                                <span className="spec-value" style={{ 
                                  color: '#4CAF50', 
                                  fontSize: '0.9em',
                                  fontWeight: '500'
                                }}>
                                  {hours}h {minutes}m
                                </span>
                              );
                            })()
                          ) : (
                            <span 
                              className="spec-value" 
                              style={{ 
                                color: '#4CAF50', 
                                fontSize: '0.9em', 
                                cursor: 'pointer',
                                textDecoration: 'underline'
                              }}
                              onClick={() => navigate('/compare-screens', {
                                state: { returnTo: '/battery-selection' }
                              })}
                            >
                              Select a screen →
                            </span>
                          )}
                        </div>
                        <div className="spec-row" style={{
        display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Capacity:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.capacity}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Type:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.type}</span>
                        </div>
                        <div className="spec-row" style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '4px 0',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Watt Hours:</span>
                          <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.wattHours || '384Wh'}</span>
                        </div>

                        {/* Expandable specs section */}
                        {expandedCards.has(batteryOption.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Voltage:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.nominalVoltage || '12.8V'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Dimensions:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.dimensions || '5.9" x 3.9" x 5.1"'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Weight:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.weight || '6.8 lbs'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Cycles:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.chargeCycles || '800+'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Warranty:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.warranty || '3 years'}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Bluetooth:</span>
                              <span className="spec-value" style={{ 
                                color: batteryOption.hasBluetooth ? '#4CAF50' : '#ffffff', 
                                fontSize: '0.9em',
                                fontWeight: batteryOption.hasBluetooth ? '500' : 'normal'
                              }}>
                                {batteryOption.hasBluetooth ? 'Yes' : 'No'}
                              </span>
                            </div>
                          </>
                        ) : (
                          // Show a faded version of the 5th feature (Voltage) when not expanded
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6,
                            position: 'relative'
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Voltage:</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{batteryOption.nominalVoltage || '12.8V'}</span>
                            {/* Gradient overlay to fade the content */}
                            <div style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background: 'linear-gradient(to bottom, rgba(26, 26, 26, 0) 0%, rgba(26, 26, 26, 0.8) 100%)',
                              pointerEvents: 'none'
                            }} />
                          </div>
                        )}
                        
                        {/* Show/Hide button */}
        <button
                          onClick={(e) => toggleCardExpansion(batteryOption.id, e)}
          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
            display: 'flex',
            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {expandedCards.has(batteryOption.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
        </button>
                      </div>

        <button
                        onClick={() => {
                          // First select the battery
                          handleBatterySelect(batteryOption);
                          
                          // Then navigate to next step - use direct navigate if available
                          if (isInModal) {
                            setTimeout(() => nextStep(), 50);
                          } else {
                            // Use the current variant we already calculated above
                            const batteryToSave = {
                              ...currentVariant,
                              image: batteryOption.image,
                              fallbackImage: batteryOption.fallbackImage,
                              type: batteryOption.type,
                              capacity: batteryOption.capacity,
                              nominalVoltage: batteryOption.nominalVoltage,
                              wattHours: batteryOption.wattHours,
                              hasChargerOption: batteryOption.hasChargerOption,
                              includeCharger: includeCharger
                            };
                            
                            console.log("Navigating with battery:", batteryToSave);
                            console.log("Preserving existing state:", builderState);
                                  
                            // Use React Router navigate directly to custom-builder with state
                            navigate('/custom-builder', { 
                              state: {
                                ...builderState,
                                battery: batteryToSave
                              }
                            });
                          }
                        }}
          style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: isSelected ? '#4CAF50' : 'rgba(0, 0, 0, 0.5)',
                          border: isSelected ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
                          borderRadius: '4px',
            color: '#fff',
            cursor: 'pointer',
                          fontSize: '0.85em',
                          marginTop: '6px'
          }}
        >
                        {isSelected ? 'Selected ✓' : 'Select Battery'}
        </button>
                    </div>
                  </div>
                );
              })}
            </GridContainer>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {modalImage.isOpen && (
      <ImageModal 
        isOpen={modalImage.isOpen}
        imageUrl={modalImage.url}
        altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
      />
      )}
    </div>
  );
};

export default BatterySelection;
